import { makeAutoObservable } from 'mobx'
import { GpsBounds, Place, PlacePreference } from 'infra/type.ts'

export const isValidPlace = (value: unknown): value is Place => {
  if (!value || typeof value !== 'object') return false
  const place = value as Partial<Place>
  return (
    typeof place.id === 'number' &&
    typeof place.name === 'string' &&
    !!place.coord &&
    Number.isFinite(Number(place.coord.lat)) &&
    Number.isFinite(Number(place.coord.lng)) &&
    Array.isArray(place.categories) &&
    Array.isArray(place.references)
  )
}

class PlacesStore {
  data: Map<Number, Place> = new Map()

  constructor() {
    makeAutoObservable(this)
  }

  remove(place: Place) {
    this.data.delete(place.id)
  }

  save(places: Place[]) {
    places.forEach((p) => {
      if (!isValidPlace(p)) {
        console.warn('[PlacesStore] Ignoring malformed place')
        return
      }
      this.data.set(p.id, p)
    })
  }

  get places(): Place[] {
    return Array.from(this.data.values())
  }

  getPlaceById(placeId: number): Place | undefined {
    return this.data.get(placeId)
  }

  getPlacesWithinBounds(bounds: GpsBounds | null): Place[] {
    if (!bounds) return []
    return Array.from(this.data.values()).filter((p) => {
      return (
        bounds.minLat <= Number(p.coord.lat) &&
        Number(p.coord.lat) <= bounds.maxLat &&
        bounds.minLng <= Number(p.coord.lng) &&
        Number(p.coord.lng) <= bounds.maxLng
      )
    })
  }

  updatePreference(placeId: number, preference: PlacePreference) {
    const place = this.data.get(placeId)
    if (!place) {
      console.error('Could not find the place to update preference!')
      return
    }
    place.preference = preference
  }
}

export const placesStore = new PlacesStore()
